'use client';

import { useEffect, useState } from 'react';

/**
 * AuthShell — gebrandete Karten-Hülle für Auth-Flow-Seiten (Reset-Passwort etc.).
 *
 * Rendert dieselbe Karte wie Signin/Signup (`.auth-card auth-card-glass max-w-md`),
 * lädt Domain-Daten (Logo, Name, Beschreibung) + Login-Hintergrundbild via
 * `/api/domaincheck` (Fallback `/api/theme`) und räumt beim Unmount sauber auf.
 * Die Domain-/Background-Logik ist 1:1 aus SigninClient.jsx übernommen, damit das
 * Layout identisch zu Login/Registrierung aussieht.
 *
 * Props:
 *  - title?: string     seiten-spezifische Überschrift (z. B. "Passwort zurücksetzen")
 *  - subtitle?: string  Hinweistext unter der Überschrift
 *  - documentTitle?: string  Prefix für <title> (z. B. "Passwort zurücksetzen")
 *  - children: das seiten-spezifische Formular
 */
export default function AuthShell({ title, subtitle, documentTitle, children }) {
  const [domainInfo, setDomainInfo] = useState(null);
  const [isLoadingDomain, setIsLoadingDomain] = useState(true);

  // --- Cache (24h, pro Hostname) — identisch zu SigninClient ---
  const getCacheKey = () => {
    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    return `xoffi_domain_cache_${hostname}`;
  };

  const getCachedDomainData = () => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data.domainInfo;
        }
        localStorage.removeItem(getCacheKey());
      }
    } catch {
      // Cache read failed silently
    }
    return null;
  };

  const setCachedDomainData = (info) => {
    try {
      localStorage.setItem(
        getCacheKey(),
        JSON.stringify({
          domainInfo: info,
          timestamp: Date.now(),
          domain: window.location.hostname,
        }),
      );
    } catch {
      // Cache write failed silently
    }
  };

  // --- Hintergrundbild via injiziertes <style> (URL relativ zum Dokument) ---
  const injectBackground = (url, { fade } = { fade: true }) => {
    const absoluteUrl = url.startsWith('/')
      ? `${window.location.origin}${url}`
      : url;
    let styleEl = document.getElementById('xoffi-auth-bg-style');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'xoffi-auth-bg-style';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `body.auth-bg-custom::before { background-image: url('${absoluteUrl}') !important; }`;
    document.documentElement.style.setProperty(
      '--auth-bg-image',
      `url('${absoluteUrl}')`,
    );
    if (fade) {
      setTimeout(() => document.body.classList.add('bg-loaded'), 150);
    } else {
      document.body.classList.add('bg-loaded');
    }
  };

  const setBackgroundImageFromCache = (url) => injectBackground(url, { fade: false });

  const preloadAndSetBackground = (url) => {
    const img = new Image();
    img.onload = () => injectBackground(url, { fade: true });
    img.onerror = () => {};
    img.src = url;
  };

  const setLoginBackgroundFallback = (backgroundPath) => {
    let finalUrl;
    if (
      backgroundPath.startsWith('/vendor/') ||
      backgroundPath.startsWith('/backend/') ||
      backgroundPath.startsWith('/de/')
    ) {
      const cleanPath = backgroundPath.startsWith('/')
        ? backgroundPath.substring(1)
        : backgroundPath;
      finalUrl = `/api/external-assets/${cleanPath}`;
    } else {
      finalUrl = backgroundPath;
    }
    preloadAndSetBackground(finalUrl);
  };

  // --- Domain-Daten + Hintergrund laden (übernommen aus SigninClient:218-389) ---
  useEffect(() => {
    document.body.classList.add('auth-bg-custom');

    const cachedData = getCachedDomainData();
    if (cachedData) {
      setDomainInfo(cachedData);
      setIsLoadingDomain(false);
      if (cachedData.domain?.background_login_url) {
        setBackgroundImageFromCache(cachedData.domain.background_login_url);
      }
    }

    const loadDomainData = async () => {
      try {
        if (!cachedData) setIsLoadingDomain(true);

        // Bevorzugt echte MySQL-Daten via DomainCheck
        const domainResponse = await fetch('/api/domaincheck', {
          credentials: 'omit',
        });
        if (domainResponse.ok) {
          const domainData = await domainResponse.json();
          if (domainData.success && domainData.found_in_database) {
            const d = domainData.domain_data;
            const themeCompatibleData = {
              domain: {
                id: d.id,
                domain: d.domain,
                name: d.name,
                name_login: d.name_login,
                title: d.title,
                description:
                  d.description || `Willkommen bei ${d.name_login}`,
                logo: d.logo,
                logo_light: d.logo_light,
                logo_dark: d.logo_dark,
                logo_light_url: d.logo_light_url,
                logo_dark_url: d.logo_dark_url,
                organization: d.organisation,
                theme: d.theme,
                favicon: d.favicon,
                primary_color: d.organisation === 115 ? '#1E40AF' : '#009ef7',
                mysql_source: true,
              },
            };

            setDomainInfo(themeCompatibleData);

            if (d.background_login_url) {
              themeCompatibleData.domain.background_login_url =
                d.background_login_url;
            }
            setCachedDomainData(themeCompatibleData);

            if (d.background_login_url) {
              preloadAndSetBackground(d.background_login_url);
            }
            return;
          }
        }

        // Fallback: Theme-API
        const themeResponse = await fetch('/api/theme', { credentials: 'omit' });
        if (themeResponse.ok) {
          const themeData = await themeResponse.json();
          setDomainInfo(themeData.data);

          if (themeData.data?.domain?.background_login) {
            const cleanPath = themeData.data.domain.background_login.startsWith(
              '/',
            )
              ? themeData.data.domain.background_login.substring(1)
              : themeData.data.domain.background_login;
            if (
              cleanPath.startsWith('vendor/') ||
              cleanPath.startsWith('backend/') ||
              cleanPath.startsWith('de/')
            ) {
              themeData.data.domain.background_login_url = `/api/external-assets/${cleanPath}`;
            } else {
              themeData.data.domain.background_login_url =
                themeData.data.domain.background_login;
            }
          }
          setCachedDomainData(themeData.data);

          if (themeData.data?.domain?.background_login) {
            setLoginBackgroundFallback(themeData.data.domain.background_login);
          }
        }
      } catch {
        // Domain/Theme loading failed silently
      } finally {
        if (!cachedData) setIsLoadingDomain(false);
      }
    };

    loadDomainData();

    return () => {
      document.body.classList.remove('auth-bg-custom');
      document.body.classList.remove('bg-loaded');
      document.documentElement.style.removeProperty('--auth-bg-image');
      document.body.style.removeProperty('background-size');
      document.body.style.removeProperty('background-position');
      document.body.style.removeProperty('background-image');
      document.body.style.removeProperty('background-attachment');
      document.body.style.removeProperty('background-repeat');
    };
  }, []);

  // Dokument-Titel setzen (sobald Domain-Name bekannt ist)
  useEffect(() => {
    const brand =
      domainInfo?.domain?.title || domainInfo?.domain?.name || 'XOFFI';
    document.title = documentTitle ? `${documentTitle} | ${brand}` : brand;
  }, [domainInfo, documentTitle]);

  const brandName =
    domainInfo?.domain?.name_login || domainInfo?.domain?.title || null;
  // Bevorzugt optimierte _url-Felder, fällt aber auf Roh-Pfade zurück
  // (Backend liefert _url nur für /vendor//backend//de/-Pfade).
  const logoLightSrc =
    domainInfo?.domain?.logo_light_url ||
    domainInfo?.domain?.logo_light ||
    domainInfo?.domain?.logo ||
    null;
  const logoDarkSrc =
    domainInfo?.domain?.logo_dark_url || domainInfo?.domain?.logo_dark || null;

  return (
    <div
      className={`auth-card auth-card-glass w-full max-w-md mx-auto transition-all duration-500 ${
        isLoadingDomain ? 'opacity-80 blur-sm' : 'opacity-100'
      }`}
    >
      {/* Gebrandeter Header (Logo + Domain-Name) */}
      <div className="text-center mb-6">
        {isLoadingDomain ? (
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded-lg mb-3 w-48 mx-auto" />
          </div>
        ) : (
          <div className="animate-fade-in">
            {(logoLightSrc || logoDarkSrc) && (
              <div className="logo-container mb-4">
                {logoLightSrc && (
                  <img
                    src={logoLightSrc}
                    alt={`${brandName || 'Company'} Logo`}
                    className={`mx-auto max-h-20 w-auto transition-opacity duration-300 ${
                      logoDarkSrc ? 'dark:hidden' : ''
                    }`}
                  />
                )}
                {logoDarkSrc && (
                  <img
                    src={logoDarkSrc}
                    alt={`${brandName || 'Company'} Logo`}
                    className="mx-auto max-h-20 w-auto hidden dark:block transition-opacity duration-300"
                  />
                )}
              </div>
            )}
            {brandName && (
              <h1 className="text-xl font-semibold text-foreground transition-all duration-300">
                {brandName}
              </h1>
            )}
          </div>
        )}
      </div>

      {/* Seiten-spezifischer Kontext (z. B. "Passwort zurücksetzen") */}
      {(title || subtitle) && (
        <div className="text-center mb-6 space-y-1">
          {title && (
            <h2 className="text-lg font-semibold tracking-tight text-foreground">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
      )}

      {children}
    </div>
  );
}
